import { QuantityInput } from "@app/components/quantity-input"
import { SizeButtons } from "@app/components/size-buttons"
import { addToCartWithZCodes } from "@app/lib/cart"
import { scrollIntoView } from "@app/utils/scrollIntoView"

class ProductPage extends HTMLElement {
  type: string;

  $sizeButtons: SizeButtons | null;
  $quantityInput: QuantityInput;
  $personalisation: HTMLDivElement | null;
  $type: HTMLInputElement | null;
  $recipientName: HTMLInputElement | null;
  $recipientEmail: HTMLInputElement | null;
  $senderName: HTMLInputElement | null;
  $giftCardValueSelect: HTMLSelectElement | null;

  $addToCartButton: HTMLButtonElement;
  $addToCartError: HTMLButtonElement;
  $form: HTMLFormElement;

  constructor() {
    super();
    this.type = this.dataset.type!;

    this.$sizeButtons = this.querySelector("size-buttons");
    this.$quantityInput = this.querySelector("quantity-input")!;
    this.$personalisation = this.querySelector(".snippet-input--personalisation");
    this.$type = this.querySelector(".snippet-input--type") || null;
    this.$recipientName = this.querySelector(".snippet-input--recipient_name") || null;
    this.$recipientEmail = this.querySelector(".snippet-input--recipient_email") || null;
    this.$senderName = this.querySelector(".snippet-input--sender_name") || null;
    this.$giftCardValueSelect = this.querySelector(".snippet-input--id select") || null;

    this.$addToCartButton = this.querySelector(".add-to-cart")!;
    this.$addToCartError = this.querySelector(".add-to-cart--error")!;
    this.$form = this.querySelector(".main-product-form")!;
  }

  connectedCallback() {
    // Update other form inputs when size changes
    this.$sizeButtons?.querySelector('input[name="id"]')?.addEventListener("change", () => {
      this.resetQuantity();
      this.resetAddToCartButton();
    });

    // Form submit
    this.$form.addEventListener("submit", async (e: any) => {
      e.preventDefault();

      const formData = new FormData(e.target);
      const {
        quantity,
        personalisation,
        id: variantId,
        type,
        recipient_name,
        recipient_email,
        sender_name,
      } = Object.fromEntries(formData as any);

      // validate size/variant
      if (!variantId) {
        this.$sizeButtons?.showError();
        return;
      }

      try {
        // validate personalisation
        if (this.$personalisation) {
          switch (true) {
            case !personalisation.trim():
              window.inputError(
                this.$personalisation,
                "Please enter your personalisation.<br /><br />Please use only letters and numbers, with a maximum of 15 characters. Special characters like @, #, $, %, &, and * are not allowed. We will review the content of your personalisation and have the right to refuse the order if it is inappropriate.",
                true,
              );
              scrollIntoView(this.$personalisation, 100);
              return;
            case !personalisation.match(/^[a-zA-Z0-9]+$/):
              window.inputError(
                this.$personalisation,
                "Please use only letters and numbers, with a maximum of 15 characters. Special characters like @, #, $, %, &, and * are not allowed. We will review the content of your personalisation and have the right to refuse the order if it is inappropriate.",
              );
              scrollIntoView(this.$personalisation, 100);
              return;
            default:
              window.inputErrorClear(this.$personalisation);
          }
        }

        // validate gift card inputs
        if (this.type === "gift_card") {
          window.inputErrorClear(this.$type!);
          window.inputErrorClear(this.$recipientName!);
          window.inputErrorClear(this.$recipientEmail!);
          window.inputErrorClear(this.$senderName!);

          if (!type) {
            window.inputError(this.$type!, "Please select a type.");
            scrollIntoView(this.$type!, 100);
            return;
          }

          if (!recipient_name?.trim()) {
            window.inputError(this.$recipientName!, "Please enter the recipient's name.");
            scrollIntoView(this.$recipientName!, 100);
            return;
          }

          if (!recipient_email?.trim()) {
            window.inputError(this.$recipientEmail!, "Please enter the recipient's email address.");
            scrollIntoView(this.$recipientEmail!, 100);
            return;
          }

          if (!sender_name?.trim()) {
            window.inputError(this.$senderName!, "Please enter your name.");
            scrollIntoView(this.$senderName!, 100);
            return;
          }
        }

        // Calculate how many of Z-code/current products to add
        const sizeButton: HTMLButtonElement | null | undefined = this.$sizeButtons?.querySelector(`button[data-id='${variantId}']`)

        this.$addToCartError.innerText = "";
        this.$addToCartButton.setAttribute("disabled", "true");

        const error = await addToCartWithZCodes(
          parseInt(quantity),
          variantId,
          sizeButton?.dataset.text,
          {
            ...(personalisation && { personalisation: personalisation.toUpperCase() }),
            ...(type && { type: type }),
            ...(recipient_name && { recipient_name: recipient_name }),
            ...(recipient_email && { recipient_email: recipient_email }),
            ...(sender_name && { sender_name: sender_name }),
          },
          sizeButton?.dataset?.zId,
          sizeButton?.dataset?.zText,
          parseInt(sizeButton?.dataset?.zQuantity || "0")
        )

        if (error) {
          this.$addToCartError.innerText = error
        }

      } catch (error) {
        console.error(error);
        this.$addToCartError.innerText = "Something went wrong"
      }

      this.$addToCartButton.removeAttribute("disabled");
    });

    // Add event listener for gift card type change
    if (this.type === "gift_card" && this.$type) {
      this.$type.addEventListener("change", this.updateGiftCardValueOptions.bind(this));
      this.updateGiftCardValueOptions();
    }

    // Update gift card value options on page load
    if (this.type === "gift_card" && this.$type) {
      setTimeout(() => {
        this.updateGiftCardValueOptions();
      }, 1000);
    }
  }

  resetQuantity() {
    this.$quantityInput.reset();
  }

  resetAddToCartButton() {
    this.$addToCartButton.removeAttribute("disabled");
  }

  updateGiftCardValueOptions() {
    if (!this.$giftCardValueSelect || !this.$type) return;

    const selectedType = (this.$type.querySelector("select") as HTMLSelectElement).value.toLowerCase();
    const dropdownWrapper = this.$giftCardValueSelect.closest(".dropdown-wrapper");
    if (!dropdownWrapper) return;

    const radioItems = dropdownWrapper.querySelectorAll(".radio-item");

    radioItems.forEach((item) => {
      const itemType = item.getAttribute("data-type")?.toLowerCase();
      if (itemType === selectedType) {
        item.classList.remove("hidden");
      } else {
        item.classList.add("hidden");
      }
    });

    // Reset the selected value if it's not visible
    const selectedRadio = dropdownWrapper.querySelector(".radio-item.same-as-selected");
    if (selectedRadio?.classList.contains("hidden")) {
      // Find the first visible radio item
      const firstVisibleRadio = dropdownWrapper.querySelector(".radio-item:not(.hidden)");
      if (firstVisibleRadio) {
        (firstVisibleRadio as HTMLElement).click();
      } else {
        // If no visible options, reset the select element
        this.$giftCardValueSelect.value = "";
      }
    }
  }
}

window.customElements.define("product-page", ProductPage);
